import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Navigation, Car } from 'lucide-react';
const MAPS_QUERY = '53+Wright+Brothers+Ave+Ste+A+Livermore+CA+94551';
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${MAPS_QUERY}`;
const EMBED_URL = `https://www.google.com/maps?q=${MAPS_QUERY}&output=embed`;
type VisitUsProps = {
  /** Rendered as the section's heading. Use h1 on the dedicated /location page. */
  headingLevel?: 'h1' | 'h2';
};
export function VisitUs({
  headingLevel = 'h2'
}: VisitUsProps) {
  const Heading = headingLevel;
  return <section id="visit" aria-labelledby="visit-heading" className="py-24 px-6 md:px-12 lg:px-24 bg-[#0a0a0a] border-t border-white/10" data-id="element-112" data-mp="2l4bk95pw64pa">
      <div className="max-w-7xl mx-auto" data-id="element-113" data-mp="2c4og4c5s2ksj">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-114" data-mp="vrpx3mamqbux">
          {/* Details */}
          <div data-id="element-115" data-mp="32yltr4lky53m">
            <div className="flex items-center gap-3 mb-6" data-id="element-116" data-mp="32z3v2fn3n9ie">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-117" data-mp="330otigcjbclj" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-118" data-mp="3gzenvo7yyy67">
                Find Us
              </span>
            </div>

            <Heading id="visit-heading" className="font-bebas text-6xl md:text-7xl leading-[0.9] mb-10 text-white" data-id="element-119" data-mp="3pytecno4z8qp">
              VISIT <span className="text-[#D97E3A]" data-id="element-120" data-mp="3lx7vmlox808l">US</span>
            </Heading>

            <div className="space-y-8" data-id="element-121" data-mp="3i5nvlr3zo8v">
              <div className="flex gap-4" data-id="element-122" data-mp="3lbbewk3ai2h">
                <MapPin className="text-[#D97E3A] shrink-0 mt-1" size={24} aria-hidden="true" data-id="element-123" data-mp="3uqt79w5sh1ds" />
                <div data-id="element-124" data-mp="8ijt6w1bsk37">
                  <h3 className="font-bebas text-2xl tracking-wide text-white mb-2" data-id="element-125" data-mp="8jcuhhhimhdy">
                    Address
                  </h3>
                  <address className="font-inter text-lg text-white/70 not-italic leading-relaxed" data-id="element-126" data-mp="fnl17cepbglz">
                    53 Wright Brothers Ave, Ste A
                    <br data-id="element-127" data-mp="pjfowlxs81vm" />
                    Livermore, CA 94551
                  </address>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-128" data-mp="ubfuatwmfmzl">
                <Phone className="text-[#D97E3A] shrink-0 mt-1" size={24} aria-hidden="true" data-id="element-129" data-mp="z9r5b7ynk3je" />
                <div data-id="element-130" data-mp="139rkwvxhmks1">
                  <h3 className="font-bebas text-2xl tracking-wide text-white mb-2" data-id="element-131" data-mp="13d6yfubijlrc">
                    Phone
                  </h3>
                  <a href="tel:+19252920433" className="font-inter text-lg text-white/70 hover:text-[#D97E3A] transition-colors" data-id="element-132" data-mp="1da1ve8s51e3i">
                    (925) 292-0433
                  </a>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-133" data-mp="37h43s1rb7acf">
                <Clock className="text-[#D97E3A] shrink-0 mt-1" size={24} aria-hidden="true" data-id="element-134" data-mp="37ixafh1x9ukf" />
                <div data-id="element-135" data-mp="2xr1cc5zu64g7">
                  <h3 className="font-bebas text-2xl tracking-wide text-white mb-2" data-id="element-136" data-mp="2xscey2hjp65q">
                    Hours
                  </h3>
                  <p className="font-inter text-lg text-white/70" data-id="element-137" data-mp="2sqdbytppwb9b">
                    Open Daily · 7:00 AM – 2:00 PM
                  </p>
                </div>
              </div>

              <div className="flex gap-4" data-id="element-138" data-mp="3lhol0thksy3t">
                <Car className="text-[#D97E3A] shrink-0 mt-1" size={24} aria-hidden="true" data-id="element-139" data-mp="3ghsfuqx570mo" />
                <div data-id="element-140" data-mp="3bipouadzyuxp">
                  <h3 className="font-bebas text-2xl tracking-wide text-white mb-2" data-id="element-141" data-mp="252vq1et4w7e9">
                    Parking & Neighborhood
                  </h3>
                  <p className="font-inter text-base text-white/60 leading-relaxed max-w-md" data-id="element-142" data-mp="2030zxdh6nlu9">
                    We're tucked into the Wright Brothers Ave business park near
                    the Livermore Municipal Airport, with free surface parking
                    right outside the door. Plenty of room for a quick pickup or
                    a slow morning — and an easy stop on your way to or from the
                    580.
                  </p>
                </div>
              </div>
            </div>

            <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors group" data-id="element-143" data-mp="bjv3qpjc48ih">
              <Navigation size={20} aria-hidden="true" data-id="element-144" data-mp="6m3czyufesfv" />
              GET DIRECTIONS
            </a>
          </div>

          {/* Map */}
          <div className="relative w-full aspect-square lg:aspect-[4/5] border border-white/10" data-id="element-145" data-mp="2rar5hb8ny5bj">
            <iframe title="Map showing Grounded at 53 Wright Brothers Ave, Ste A, Livermore, CA 94551" src={EMBED_URL} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 w-full h-full grayscale-[0.4]" style={{
            border: 0
          }} data-id="element-146" data-mp="19c36thjw7n2c" />
          </div>
        </motion.div>
      </div>
    </section>;
}